import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { softPlatformIcon } from './socialSoftPalette';
import { LinkedInIcon, FacebookIcon, InstagramIcon, TwitterIcon, CalendarIcon, ClockIcon, PostIcon, TipIcon, WarningIcon, ErrorIcon, ConnectionLinkIcon, InfoIcon, SaveDiskIcon, CheckIcon, DocumentIcon } from '../icons';

interface PostSchedulerModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialContent?: string;
  initialAttachments?: Array<{ type: 'image' | 'video' | 'file'; url: string; name?: string }>;
}

const PostSchedulerModal: React.FC<PostSchedulerModalProps> = ({
  isOpen,
  onClose,
  userId,
  initialContent = '',
  initialAttachments = []
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['linkedin']);
  const [loading, setLoading] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState<string[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);

  // Check which platforms are connected
  useEffect(() => {
    const checkConnections = async () => {
      if (!userId) return;
      setLoadingConnections(true);
      try {
        const profileDoc = await getDoc(doc(db, 'socialProfiles', userId));
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          const connected: string[] = [];
          if (data.linkedin?.accessToken) connected.push('linkedin');
          if (data.facebook?.accessToken) connected.push('facebook');
          if (data.instagram?.accessToken) connected.push('instagram');
          if (data.twitter?.accessToken) connected.push('twitter');
          setConnectedPlatforms(connected);
        }
      } catch (error) {
        console.error('Error checking connections:', error);
      } finally {
        setLoadingConnections(false);
      }
    };
    checkConnections();
  }, [userId]);

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, iconColor: softPlatformIcon('linkedin') },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, iconColor: softPlatformIcon('facebook') },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, iconColor: softPlatformIcon('instagram') },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, iconColor: softPlatformIcon('twitter') },
  ];

  const handleSchedulePost = async () => {
    if (!initialContent.trim() || selectedPlatforms.length === 0) {
      alert('Selecteer minimaal 1 platform');
      return;
    }

    setLoading(true);
    try {
      // Check post limit before scheduling
      const { default: postLimitService } = await import('../../services/postLimitService');
      await postLimitService.enforcePostLimit(userId);
      
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);

      // Extract image URLs from attachments
      const imageUrls = initialAttachments
        .filter(att => att.type === 'image')
        .map(att => att.url);

      const newPost = {
        userId,
        content: initialContent,
        platforms: selectedPlatforms,
        scheduledFor: scheduledDateTime,
        status: 'scheduled',
        imageUrls: imageUrls,
        autoShareToNewsFeed: true,
        createdAt: new Date()
      };

      await addDoc(collection(db, 'scheduledPosts'), newPost);
      
      const formattedTime = scheduledDateTime.toLocaleString('nl-NL', {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      alert(`✅ Post ingepland!\n\n📅 Wordt automatisch geplaatst op:\n${formattedTime}\n\n💡 Je kunt de post bekijken in Social Media Hub → Content Queue`);
      onClose();
    } catch (error: any) {
      console.error('Error scheduling post:', error);
      if (error.message?.includes('Post limit')) {
        alert('⚠️ ' + error.message);
      } else {
        alert('❌ Fout bij inplannen van post');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  // Generate time options (every 15 minutes)
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarIcon size={24} weight="regular" className="text-gray-600" />
            <span>Post Plannen</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Post Preview */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><PostIcon size={16} weight="regular" className="text-gray-600" /> Post Preview</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {initialContent || 'Geen content'}
            </p>
            
            {/* Attachments Preview */}
            {initialAttachments.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-gray-600 mb-2">Bijlagen:</p>
                <div className="flex gap-2 flex-wrap">
                  {initialAttachments.map((att, idx) => {
                    if (att.type === 'image') {
                      return (
                        <img 
                          key={idx}
                          src={att.url}
                          alt={att.name || 'Afbeelding'}
                          className="w-20 h-20 object-cover rounded border"
                        />
                      );
                    }
                    return (
                      <div key={idx} className="text-xs bg-white border rounded px-2 py-1 flex items-center gap-1">
                        {att.type === 'file' && <DocumentIcon size={12} weight="regular" className="text-gray-500" />}
                        {att.name || att.type}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Helpful hints */}
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 space-y-1">
              <p className="flex items-center gap-1"><TipIcon size={14} weight="regular" className="text-gray-500" /> <strong>Tips:</strong></p>
              <ul className="list-disc list-inside ml-2 space-y-0.5">
                <li>YouTube links worden automatisch als video preview getoond</li>
                <li>Afbeeldingen worden automatisch geüpload naar LinkedIn</li>
              </ul>
            </div>
          </div>

          {/* Date & Time Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <CalendarIcon size={16} weight="regular" className="text-gray-600" /> Datum
              </label>
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <ClockIcon size={16} weight="regular" className="text-gray-600" /> Tijd
              </label>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488]"
              >
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Platforms (selecteer minimaal 1)
            </label>
            
            {/* Warning if no platforms connected */}
            {!loadingConnections && connectedPlatforms.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <WarningIcon size={18} weight="regular" className="text-amber-500 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Geen accounts gekoppeld!</strong>
                    <p className="mt-1">Je hebt nog geen social media accounts gekoppeld.</p>
                    <button 
                      onClick={() => window.location.hash = '#social-media'}
                      className="mt-2 text-amber-700 hover:text-amber-900 font-medium underline flex items-center gap-1"
                    >
                      <ConnectionLinkIcon size={14} weight="regular" /> Ga naar accounts koppelen
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              {platforms.map(platform => {
                const isConnected = connectedPlatforms.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={`
                      p-4 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 relative
                      ${selectedPlatforms.includes(platform.id)
                        ? 'border-[#7A9488] bg-stone-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }
                      ${!isConnected ? 'opacity-60' : ''}
                    `}
                  >
                    <platform.Icon size={28} weight="regular" style={{ color: platform.iconColor }} />
                    <div className="text-left flex-1">
                      <div className="font-semibold text-gray-900">{platform.name}</div>
                      {isConnected ? (
                        selectedPlatforms.includes(platform.id) ? (
                          <div className="text-xs text-[#4F6660] flex items-center gap-0.5"><CheckIcon size={12} weight="bold" /> Geselecteerd</div>
                        ) : (
                          <div className="text-xs text-[#4F6660] flex items-center gap-0.5"><CheckIcon size={12} weight="bold" /> Gekoppeld</div>
                        )
                      ) : (
                        <div className="text-xs text-amber-600 flex items-center gap-0.5"><WarningIcon size={12} weight="regular" /> Niet gekoppeld</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Selected but not connected warning */}
            {selectedPlatforms.some(p => !connectedPlatforms.includes(p)) && !loadingConnections && (
              <div className="mt-3 bg-stone-50 border border-stone-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <ErrorIcon size={18} weight="regular" className="text-[#8B7A7A] flex-shrink-0" />
                  <div className="text-sm text-stone-700">
                    <strong>Let op:</strong> Je hebt platforms geselecteerd die niet gekoppeld zijn. 
                    De post zal mislukken voor deze platforms.
                    <button 
                      onClick={() => window.location.hash = '#social-media'}
                      className="ml-1 text-[#4F6660] hover:text-[#3d5248] font-medium underline"
                    >
                      Koppel je accounts →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-sm text-stone-700">
            <p className="font-semibold mb-1 flex items-center gap-1"><InfoIcon size={16} weight="regular" /> Automatisch Plaatsen</p>
            <p>Je post wordt automatisch geplaatst op de geselecteerde platforms op de gekozen datum en tijd.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSchedulePost}
            disabled={loading || selectedPlatforms.length === 0}
            className="px-6 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Inplannen...</span>
              </>
            ) : (
              <>
                <SaveDiskIcon size={16} weight="regular" />
                <span>Opslaan in Queue</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostSchedulerModal;

